import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDiscoveryClient } from '../api/context';
import type { CreatorListFilters } from '../api/types';

export const registryKey = (filters: CreatorListFilters) =>
  ['discovery', 'registry', 'list', filters] as const;

export const registryCountsKey = ['discovery', 'registry', 'counts'] as const;

/**
 * The team-wide creator list. Paginated server-side — the view it replaces
 * fetched the entire global enrichment cache in one unbounded response.
 */
export function useCreatorRegistry(filters: CreatorListFilters) {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: registryKey(filters),
    queryFn: () => client.registry.list(filters),
    staleTime: 15_000,
    placeholderData: (prev) => prev, // keep the table steady while paging
  });
}

export function useCreatorCounts() {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: registryCountsKey,
    queryFn: () => client.registry.counts(),
    staleTime: 30_000,
  });
}

/** Invalidate every registry view plus the folder lead tables that mirror it. */
function useRegistryInvalidator() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['discovery', 'registry'] });
    qc.invalidateQueries({ queryKey: ['discovery', 'leads'] });
  };
}

export function useSuppressCreator() {
  const client = useDiscoveryClient();
  const invalidate = useRegistryInvalidator();
  return useMutation({
    mutationFn: ({ username, reason }: { username: string; reason?: string }) =>
      client.registry.suppress(username, reason),
    onSuccess: invalidate,
  });
}

export function useUnsuppressCreator() {
  const client = useDiscoveryClient();
  const invalidate = useRegistryInvalidator();
  return useMutation({
    mutationFn: (username: string) => client.registry.unsuppress(username),
    onSuccess: invalidate,
  });
}

/** Drop a creator from every folder at once, not one membership at a time. */
export function useRemoveFromAllFolders() {
  const client = useDiscoveryClient();
  const invalidate = useRegistryInvalidator();
  return useMutation({
    mutationFn: (username: string) => client.registry.removeFromAllFolders(username),
    onSuccess: invalidate,
  });
}

export function useRefreshRegistryStatuses() {
  const client = useDiscoveryClient();
  const invalidate = useRegistryInvalidator();
  return useMutation({
    mutationFn: (usernames: string[]) => client.registry.refreshStatus(usernames),
    onSuccess: invalidate,
  });
}
